import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, RotateCw, AlertTriangle, Globe, Stethoscope, CheckCircle2, Clock, Hourglass, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api } from "@/lib/api";

/**
 * API-Football (api-sports.io) import card — iter63b Phase B.
 *
 * Mirrors GotSportImportCard. Custom tournaments only. The site admin
 * controls the per-league `api_football_enabled` flag that hides this card
 * entirely when off — the EspnSyncTab does the gating; this component just
 * renders when mounted.
 *
 * Flow:
 *   1. Admin enters API-Football League ID + Season → click "Preview"
 *   2. Backend fetches fixtures, returns counts/samples (no DB write)
 *   3. Admin confirms → backend wipes + replaces teams/matches
 *
 * Useful league IDs (free tier):
 *   1   World Cup        2026 (current)
 *   4   Euro             2024
 *   8   Women's WC       2023
 *   2   UEFA Champions   2025
 */
export default function ApiFootballImportCard({ leagueId, tournament, onImported }) {
    const [apifLeagueId, setApifLeagueId] = useState(tournament?.api_football_league_id || "");
    const [season, setSeason] = useState(tournament?.api_football_season || new Date().getFullYear());
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);
    // iter87.172 — In-app confirm modal replaces native window.confirm()
    // which mobile Safari + Chrome silently suppressed on this surface.
    const [confirmOpen, setConfirmOpen] = useState(false);
    // iter84.57.2 — Sync diagnosis modal state. `diag` is the response from
    // `/api-football/sync-diagnosis` and gates the modal open/closed.
    const [diag, setDiag] = useState(null);
    // iter84.57.11 — Passive per-stage coverage snapshot. Fetched once on
    // mount (and after Refresh / Resync) so the operator can see at-a-glance
    // how much of the knockout bracket upstream has populated, without
    // having to open the Sync Diagnosis modal. Reads local scheduled_matches
    // only — no upstream API call → safe to fetch eagerly.
    const [coverage, setCoverage] = useState(null);

    const fetchCoverage = useCallback(async () => {
        if (!tournament?.api_football_league_id) return;
        try {
            const r = await api.apiFootballCoverage(leagueId, tournament?.id);
            setCoverage(r);
        } catch (_err) {
            // Coverage chip is decorative — silently no-op on failure so a
            // momentary backend hiccup doesn't break the card itself.
        }
    }, [leagueId, tournament?.id, tournament?.api_football_league_id]);

    useEffect(() => { fetchCoverage(); }, [fetchCoverage]);

    const handlePreview = async () => {
        if (!apifLeagueId || !season) {
            toast.error("Enter both League ID and Season first.");
            return;
        }
        setBusy("preview");
        try {
            const r = await api.apiFootballPreview(leagueId, apifLeagueId, season, tournament?.id);
            setPreview(r);
            if (r.match_count === 0) {
                toast.info("No fixtures returned — double-check the League ID + Season.");
            } else {
                toast.success(`Found ${r.team_count} teams · ${r.match_count} fixtures (${r.played_count} played).`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Preview failed.");
        } finally {
            setBusy(null);
        }
    };

    const handleImport = async () => {
        if (!preview) return;
        setConfirmOpen(false);
        setBusy("import");
        try {
            const r = await api.apiFootballImport(
                leagueId, apifLeagueId, season, preview.league_name, tournament?.id,
            );
            toast.success(`Imported ${r.team_count} teams + ${r.match_count} matches.`);
            setPreview(null);
            onImported?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import failed.");
        } finally {
            setBusy(null);
        }
    };

    const handleRefresh = async () => {
        setBusy("refresh");
        try {
            const r = await api.apiFootballRefresh(leagueId, tournament?.id);
            // iter84.57.18 — Surface `renamed_placeholders` too. Pre-fix the
            // toast only counted scores + new-played, so when Resync re-bound
            // stuck KO rows (e.g. Mexico/Ecuador finally getting a
            // `home_team_id` after auto_rename ran) the operator saw the
            // misleading "Already up to date" message even though the
            // diagnosis modal had warned "the poller didn't propagate" and
            // the click had in fact propagated 7 names.
            const parts = [];
            if (r.updated_scores > 0) parts.push(`${r.updated_scores} score(s) updated`);
            if (r.new_played_matches > 0) parts.push(`${r.new_played_matches} newly played`);
            if (r.renamed_placeholders > 0) parts.push(`${r.renamed_placeholders} placeholder(s) re-bound`);
            const msg = parts.length ? parts.join("; ") + "." : "Already up to date.";
            toast.success(msg);
            onImported?.();
            // iter84.57.11 — Refresh the coverage chip too so newly-inserted
            // (or newly-resolved) fixtures show up without a card remount.
            await fetchCoverage();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refresh failed.");
        } finally {
            setBusy(null);
        }
    };

    // iter84.57.2 — Sync diagnosis. Wraps "is upstream behind, am I behind,
    // is the poller alive?" into a single one-click verdict the operator
    // can act on without curl'ing three different endpoints.
    const handleDiagnose = async () => {
        setBusy("diagnose");
        try {
            const r = await api.apiFootballSyncDiagnosis(leagueId, tournament?.id);
            setDiag(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Diagnosis failed.");
        } finally {
            setBusy(null);
        }
    };

    // Click-through on the modal's remediation button — only meaningful for
    // 'api_football_refresh' (we already know how to do that one).
    const handleDiagRemediation = async () => {
        if (diag?.remediation?.kind === "api_football_refresh") {
            setDiag(null);
            await handleRefresh();
        } else if (diag?.remediation?.kind === "retry") {
            setDiag(null);
            await handleDiagnose();
        } else {
            // 'wait', 'noop', 'api_football_import', 'poller_run_now' all
            // close the modal — operator follows up out-of-band.
            setDiag(null);
        }
    };

    // Verdict → visual styling for the modal banner.
    const VERDICT_STYLE = {
        healthy:             { Icon: CheckCircle2, tone: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300", label: "Healthy" },
        pull_recommended:    { Icon: RotateCw,    tone: "border-cyan-500/50 bg-cyan-500/10 text-cyan-300",       label: "Resync recommended" },
        wait_upstream:       { Icon: Hourglass,   tone: "border-amber-500/50 bg-amber-500/10 text-amber-300",    label: "Waiting on upstream" },
        placeholder_only:    { Icon: Hourglass,   tone: "border-amber-500/50 bg-amber-500/10 text-amber-300",    label: "Bracket not yet drawn upstream" },
        poller_stale:        { Icon: Clock,       tone: "border-amber-500/50 bg-amber-500/10 text-amber-300",    label: "Poller is stale" },
        not_configured:      { Icon: AlertCircle, tone: "border-zinc-500/50 bg-zinc-500/10 text-zinc-300",       label: "Not configured" },
        upstream_unreachable:{ Icon: AlertTriangle,tone: "border-rose-500/50 bg-rose-500/10 text-rose-300",     label: "Upstream unreachable" },
    };

    const alreadyImported = !!tournament?.api_football_league_id;

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="api-football-import-card">
            <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-neon" />
                <h3 className="font-display font-extrabold uppercase text-sm">API-Football Import</h3>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-auto">Custom only · api-sports.io</span>
            </div>
            <p className="text-[11px] text-zinc-500">
                Pull fixtures + teams + scores from api-sports.io. Common IDs:
                {" "}<span className="font-mono text-neon">1</span>=World Cup,
                {" "}<span className="font-mono text-neon">4</span>=Euro,
                {" "}<span className="font-mono text-neon">2</span>=UCL,
                {" "}<span className="font-mono text-neon">8</span>=Women's WC.
                Free tier ≈ 100 reqs/day so use the refresh button sparingly.
            </p>
            <p className="text-[11px] text-volt/80 flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
                <span>
                    <span className="font-bold">Free tier limitation:</span> only seasons
                    {" "}<span className="font-mono">2022</span>–<span className="font-mono">2024</span> are accessible.
                    Use season <span className="font-mono">2022</span> for World Cup (Qatar) or
                    {" "}<span className="font-mono">2024</span> for UEFA Euro. Newer seasons need a paid plan.
                </span>
            </p>

            {alreadyImported && (
                <div className="brutal-card-light p-2 text-xs space-y-1" data-testid="api-football-current">
                    <div className="text-[10px] uppercase tracking-widest text-neon font-bold">Currently linked</div>
                    <div>
                        League <span className="font-mono">{tournament.api_football_league_id}</span>
                        {" · "}Season <span className="font-mono">{tournament.api_football_season}</span>
                        {tournament.api_football_league_name && (
                            <> · <span className="font-bold">{tournament.api_football_league_name}</span></>
                        )}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                        Last synced: {tournament.api_football_last_synced_at
                            ? new Date(tournament.api_football_last_synced_at).toLocaleString()
                            : "never"}
                    </div>
                    {coverage?.configured && coverage.stages?.length > 0 && (
                        <div
                            className="flex items-center gap-1 flex-wrap text-[10px]"
                            data-testid="api-football-coverage-chip"
                            title="Per-stage knockout coverage. Each pill shows current / canonical-bracket-size. After iter84.57.9 the poller auto-inserts new fixtures as upstream publishes them — these numbers tick up on their own."
                        >
                            <span className="text-zinc-500 uppercase tracking-widest mr-1">Coverage:</span>
                            {coverage.stages.map((s) => {
                                const klass =
                                    s.status === "complete" ? "border-emerald-500/50 text-emerald-300"
                                    : s.status === "complete_unresolved" ? "border-cyan-500/50 text-cyan-300"
                                    : s.status === "partial" ? "border-amber-500/50 text-amber-300"
                                    : s.status === "overflow" ? "border-rose-500/50 text-rose-300"
                                    : "border-zinc-700 text-zinc-500";
                                return (
                                    <span
                                        key={s.stage}
                                        data-testid={`api-football-coverage-${s.stage}`}
                                        data-coverage-status={s.status}
                                        className={`pill border font-mono font-bold uppercase py-0.5 px-1.5 ${klass}`}
                                    >
                                        {s.label} {s.current}/{s.expected}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            data-testid="api-football-refresh-btn"
                            size="sm" variant="outline" type="button"
                            onClick={handleRefresh} disabled={busy === "refresh"}
                            className="text-xs"
                        >
                            <RotateCw className="w-3 h-3 mr-1" /> {busy === "refresh" ? "Refreshing…" : "Refresh scores"}
                        </Button>
                        <Button
                            data-testid="api-football-sync-diagnosis-btn"
                            size="sm" variant="outline" type="button"
                            onClick={handleDiagnose} disabled={busy === "diagnose"}
                            className="text-xs"
                            title="One-shot diagnosis: compares upstream vs. local fixture counts and tells you whether to resync, wait for upstream, or kick the poller."
                        >
                            <Stethoscope className="w-3 h-3 mr-1" /> {busy === "diagnose" ? "Checking…" : "Sync diagnosis"}
                        </Button>
                    </div>
                </div>
            )}

            <div className="flex gap-2 items-end">
                <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">League ID</label>
                    <input
                        data-testid="api-football-league-id-input"
                        type="number" value={apifLeagueId}
                        onChange={e => { setApifLeagueId(e.target.value); setPreview(null); }}
                        placeholder="e.g. 1"
                        className="w-full bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-neon focus:outline-none"
                    />
                </div>
                <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">Season</label>
                    <input
                        data-testid="api-football-season-input"
                        type="number" value={season}
                        onChange={e => { setSeason(e.target.value); setPreview(null); }}
                        placeholder="e.g. 2026"
                        className="w-full bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-neon focus:outline-none"
                    />
                </div>
                <Button
                    data-testid="api-football-preview-btn"
                    type="button" onClick={handlePreview}
                    disabled={!apifLeagueId || !season || busy === "preview"}
                    className="brutal-btn-ghost text-xs"
                >
                    {busy === "preview" ? "…" : "Preview"}
                </Button>
            </div>

            {preview && (
                <div className="brutal-card-light p-2 space-y-2" data-testid="api-football-preview">
                    <div className="text-[10px] uppercase tracking-widest text-neon">Preview</div>
                    {preview.league_name && (
                        <div className="text-xs"><span className="font-bold">{preview.league_name}</span> · Season {preview.season}</div>
                    )}
                    <div className="text-xs flex gap-3 flex-wrap">
                        <span>👥 {preview.team_count} teams</span>
                        <span>⚽ {preview.match_count} matches</span>
                        <span>✅ {preview.played_count} already played</span>
                    </div>
                    {preview.sample_teams?.length > 0 && (
                        <div className="text-[10px] text-zinc-500">
                            Sample teams: {preview.sample_teams.join(", ")}
                        </div>
                    )}
                    <div className="flex items-start gap-2 text-[10px] text-volt p-1.5 border border-volt/40 rounded">
                        <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
                        <span>
                            By importing, you confirm you have permission to use this data for your
                            league. Importing will wipe + replace this tournament's teams,
                            matches, and schedule.
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            data-testid="api-football-cancel-btn"
                            size="sm" variant="ghost" type="button"
                            onClick={() => setPreview(null)}
                            disabled={busy === "import"}
                        >Cancel</Button>
                        <Button
                            data-testid="api-football-confirm-import-btn"
                            size="sm" type="button"
                            onClick={() => setConfirmOpen(true)}
                            disabled={busy === "import" || preview.match_count === 0}
                            className="brutal-btn-primary"
                        >
                            <Upload className="w-3 h-3 mr-1" />
                            {busy === "import" ? "Importing…" : "Confirm + Wipe + Import"}
                        </Button>
                    </div>
                </div>
            )}

            {/* iter87.172 — In-app confirm modal (replaces window.confirm
                which mobile Safari + Chrome silently blocked). */}
            {confirmOpen && preview && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    onClick={() => setConfirmOpen(false)}
                    data-testid="api-football-confirm-modal"
                >
                    <div
                        className="brutal-card max-w-md w-full p-5"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle className="w-5 h-5 text-heat shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-bold uppercase tracking-widest text-white mb-1">Wipe + Import?</h3>
                                <p className="text-[12px] text-zinc-400 leading-snug">
                                    This will <span className="text-heat font-bold">WIPE</span> your current teams + matches + schedule and replace them with{" "}
                                    <span className="text-white font-bold">{preview.team_count}</span> teams and{" "}
                                    <span className="text-white font-bold">{preview.match_count}</span> matches from API-Football.
                                </p>
                                <p className="text-[11px] text-zinc-500 italic mt-2">This cannot be undone.</p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <Button
                                size="sm" variant="ghost" type="button"
                                onClick={() => setConfirmOpen(false)}
                                data-testid="api-football-confirm-modal-cancel"
                            >Cancel</Button>
                            <Button
                                size="sm" type="button"
                                onClick={handleImport}
                                data-testid="api-football-confirm-modal-proceed"
                                className="brutal-btn-primary"
                            >
                                Yes, wipe & import
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* iter84.57.2 — Sync Diagnosis modal. Opens when `diag` is set
                (via handleDiagnose). Shows the verdict + reconciliation
                counts + a remediation button matched to the verdict. */}
            <Dialog open={!!diag} onOpenChange={(v) => { if (!v) setDiag(null); }}>
                <DialogContent className="brutal-card max-w-md" data-testid="api-football-sync-diagnosis-modal">
                    {diag && (() => {
                        const v = VERDICT_STYLE[diag.verdict] || VERDICT_STYLE.not_configured;
                        const { Icon } = v;
                        return (
                            <>
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-2 font-display uppercase text-base">
                                        <Stethoscope className="w-4 h-4 text-neon" /> Sync Diagnosis
                                    </DialogTitle>
                                    <DialogDescription className="text-[11px] text-zinc-400">
                                        {diag.tournament_name ? `Tournament: ${diag.tournament_name}` : null}
                                    </DialogDescription>
                                </DialogHeader>

                                <div
                                    className={`border p-3 rounded-sm flex items-start gap-2 ${v.tone}`}
                                    data-testid={`api-football-sync-diagnosis-verdict-${diag.verdict}`}
                                >
                                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                                    <div className="flex-1">
                                        <div className="font-bold uppercase tracking-wider text-[10px]">{v.label}</div>
                                        <p className="text-[12px] mt-1 leading-relaxed">{diag.summary}</p>
                                    </div>
                                </div>

                                <div className="text-[11px] text-zinc-400 grid grid-cols-2 gap-x-3 gap-y-1">
                                    <div>Upstream fixtures:</div>
                                    <div className="font-mono text-white">
                                        {diag.upstream_fixture_count ?? "?"}
                                        {diag.upstream_resolved_count != null && diag.upstream_placeholder_count != null && (
                                            <span className="text-zinc-500 text-[10px] ml-1">
                                                ({diag.upstream_resolved_count} resolved · {diag.upstream_placeholder_count} TBD)
                                            </span>
                                        )}
                                    </div>
                                    <div>Local fixtures:</div>
                                    <div className="font-mono text-white">{diag.local_fixture_count}</div>
                                    {diag.placeholder_count > 0 && (<>
                                        <div>TBD placeholders:</div>
                                        <div className="font-mono text-amber-400">{diag.placeholder_count}</div>
                                    </>)}
                                    {diag.poller_age_s != null && (<>
                                        <div>Poller age:</div>
                                        <div className={`font-mono ${diag.poller_stale ? "text-amber-400" : "text-white"}`}>
                                            {diag.poller_age_s}s {diag.poller_stale ? "(stale)" : ""}
                                        </div>
                                    </>)}
                                    {diag.poller_enabled === false && (<>
                                        <div>Poller enabled:</div>
                                        <div className="font-mono text-rose-400">no (env-disabled)</div>
                                    </>)}
                                    {(diag.poller_last_renamed != null
                                      || diag.poller_last_updated_scores != null
                                      || diag.poller_last_new_played != null) && (<>
                                        <div className="col-span-2 text-[10px] uppercase tracking-widest text-zinc-500 pt-1 border-t border-zinc-800">
                                            Last poller pass
                                        </div>
                                        <div>Renamed:</div>
                                        <div
                                            className="font-mono text-white"
                                            data-testid="api-football-sync-diagnosis-poller-renamed"
                                        >
                                            {diag.poller_last_renamed ?? 0}
                                        </div>
                                        <div>Scores updated:</div>
                                        <div className="font-mono text-white">{diag.poller_last_updated_scores ?? 0}</div>
                                        <div>New played:</div>
                                        <div className="font-mono text-white">{diag.poller_last_new_played ?? 0}</div>
                                    </>)}
                                </div>

                                {diag.upstream_error && (
                                    <div className="text-[11px] text-rose-300 italic">
                                        Upstream error: {diag.upstream_error}
                                    </div>
                                )}

                                <DialogFooter>
                                    <Button
                                        data-testid="api-football-sync-diagnosis-remediation-btn"
                                        size="sm" variant="outline" type="button"
                                        onClick={handleDiagRemediation}
                                        className="text-xs"
                                        disabled={busy === "refresh"}
                                    >
                                        {diag.remediation.label}
                                    </Button>
                                </DialogFooter>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}
